// @ts-nocheck

import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router';
import screenfull from 'screenfull';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { useViewportContext } from '../../contexts/ViewportContext';
import usePictureInPicture from '../../hooks/usePictureInPicture';
import { isEmbedded, isIOS } from '../../lib/useragent';
import { isMultiviewRoute } from '../../utils/routes/isMultiview';

import { panelStatuses, basketStatuses } from './constants';
import ImmersiveBasketContainer from './containers/ImmersiveBasketContainer';
import ImmersiveOutcomesListContainer from './containers/ImmersiveOutcomesListContainer';
import PanelContainer from './containers/panelContainer';
import VideoPlayerContainer from './containers/VideoPlayerContainer';

const RATIO_VIDEO = 16 / 9;

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;

  ${({ $isFullscreen, $isPanelVisible, $offsetVideoLeft }) =>
    $isFullscreen
      ? css`
          height: 100%;
          text-align: center;
          /* Fix for ios with notch, video width on landscape */
          padding-top: 0;
          background: #000;

          .panel {
            transform: ${$isPanelVisible ? 'none' : 'translateX(100%)'};
          }

          .videoFullscreenButton {
            transform: ${$isPanelVisible ? 'translateX(-80%)' : 'none'};
            transition: transform 1000ms ease-out;

            @media (orientation: portrait) {
              transform: none;
              transition: none;
            }
          }

          .videoWrapper {
            transform: ${$offsetVideoLeft !== 0 ? `translateX(-${$offsetVideoLeft}px)` : 'none'};
            transition: transform 1000ms ease-out;

            @media (orientation: portrait) {
              transform: none;
              transition: none;
            }
          }
        `
      : css`
          height: auto;
          text-align: center;
          /* Fix for ios with notch, video width on landscape */
          padding-top: 56.25%;
          background: #000;

          .panel {
            transform: ${$isPanelVisible ? 'none' : 'translateX(100%)'};
          }

          .videoFullscreenButton {
            transform: none;
            transition: none;

            @media (orientation: portrait) {
              transform: none;
              transition: none;
            }
          }
          .videoWrapper {
            transform: none;
            transition: none;

            @media (orientation: portrait) {
              transform: none;
              transition: none;
            }
          }
        `}
`;

const ImmersivePlayer = (props) => {
  const {
    // Props from container
    shouldShowBasket,
    basketStatus,
    resetPanelContent,
    streamData,
    // Props
    pause,
    matchId,
    fetchUrl,
    fetchConfig,
    isHighlight = false,
  } = props;

  const { isFullscreen, setIsFullscreen } = usePictureInPicture();
  const [panelStatus, setPanelStatus] = useState(panelStatuses.HIDDEN);
  const { orientation: screenOrientation } = useViewportContext();
  const wrapperDomNodeRef = useRef();
  const location = useLocation();
  const isMultiview = isMultiviewRoute(location.pathname);

  const isImmersiveModeEnabled = useMemo(() => screenfull.isEnabled && Target.__MOBILE__ && !isIOS, []);

  // Check if the panel can be displayed
  const canDisplayPanel = useMemo(
    () => isImmersiveModeEnabled && isFullscreen && screenOrientation === 1,
    [isImmersiveModeEnabled, isFullscreen, screenOrientation],
  );

  // Check panel visibility state
  const isPanelVisible = useMemo(
    () =>
      canDisplayPanel && (panelStatus === panelStatuses.TEMPORARILY_VISIBLE || panelStatus === panelStatuses.PINNED),
    [canDisplayPanel, panelStatus],
  );

  // check if video can be move on the left when entering fullscreen
  const offsetVideoLeft = useMemo(() => {
    if (wrapperDomNodeRef.current && isPanelVisible) {
      const { offsetWidth, offsetHeight } = wrapperDomNodeRef.current;
      const videoCanBeMoveToLeft = offsetWidth / offsetHeight > RATIO_VIDEO;
      if (videoCanBeMoveToLeft) {
        const videoWidth = RATIO_VIDEO * offsetHeight;
        return (offsetWidth - videoWidth) / 2;
      }
      return 0;
    }
    return 0;
  }, [isPanelVisible]);

  const onChangeFullscreen = useCallback(() => {
    setIsFullscreen(screenfull.isFullscreen);
  }, [setIsFullscreen]);

  useEffect(() => {
    if (screenfull.isEnabled) {
      screenfull.on('change', onChangeFullscreen);
      return () => {
        screenfull.off('change', onChangeFullscreen);
      };
    }
  }, [onChangeFullscreen]);

  const shouldPanelStatusStayPinned = useMemo(
    () => basketStatus === basketStatuses.WAITING_TRADER_VALIDATION || basketStatus === basketStatuses.PENDING,
    [basketStatus],
  );

  useEffect(() => {
    if (isFullscreen) {
      if (screenOrientation === 1) {
        if (shouldPanelStatusStayPinned) {
          setPanelStatus(panelStatuses.PINNED);
        } else {
          setPanelStatus((oldStatus) =>
            oldStatus === panelStatuses.HIDDEN ? panelStatuses.TEMPORARILY_VISIBLE : oldStatus,
          );
        }
      } else {
        setPanelStatus(panelStatuses.HIDDEN);
      }
    } else {
      resetPanelContent();
      setPanelStatus(panelStatuses.HIDDEN);
    }
  }, [screenOrientation, isFullscreen, resetPanelContent, shouldPanelStatusStayPinned]);

  const enterFullscreen = useCallback(() => {
    if (screenfull.isEnabled) {
      screenfull.request(wrapperDomNodeRef.current);
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    screenfull.exit();
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  }, [isFullscreen, exitFullscreen, enterFullscreen]);

  const canTogglePanel = useMemo(
    () =>
      screenOrientation === 1 &&
      isFullscreen &&
      basketStatus !== basketStatuses.PENDING &&
      basketStatus !== basketStatuses.WAITING_TRADER_VALIDATION,
    [basketStatus, isFullscreen, screenOrientation],
  );

  const shouldHidePanel = useMemo(
    () => panelStatus === panelStatuses.TEMPORARILY_VISIBLE || panelStatus === panelStatuses.PINNED,
    [panelStatus],
  );

  const handleImmersivePlayer = useCallback(() => {
    if (canTogglePanel) {
      if (shouldHidePanel) {
        console.log(`[IMMERSIVE] - On click video, setting panelStatus to ${panelStatuses.HIDDEN}`);
        setPanelStatus(panelStatuses.HIDDEN);
      } else {
        console.log(`[IMMERSIVE] - On click video, setting panelStatus to ${panelStatuses.PINNED}`);
        setPanelStatus(panelStatuses.PINNED);
      }
    }
  }, [canTogglePanel, shouldHidePanel]);

  // Reset panel on unmount
  // /!\ reset is prevented is some cases
  useEffect(() => {
    return () => {
      resetPanelContent();
    };
  }, [resetPanelContent]);

  // Fix for safeArea bug. Override meta tag when fullscreen and only for browser
  useEffect(() => {
    if (Target.__MOBILE__ && !isEmbedded && isFullscreen) {
      const metaTagToAdd = document.createElement('meta');
      metaTagToAdd.name = 'viewport';
      // eslint-disable-next-line sonarjs/no-duplicate-string
      metaTagToAdd.id = 'override-viewport-tag';
      metaTagToAdd.setAttribute('content', 'width=device-width, minimum-scale=1, initial-scale=1');
      document.getElementsByTagName('head')[0].appendChild(metaTagToAdd);
    } else if (Target.__MOBILE__ && !isEmbedded && !isFullscreen) {
      const metaTagToDelete = document.getElementById('override-viewport-tag');
      metaTagToDelete && metaTagToDelete.parentNode.removeChild(metaTagToDelete);
    }
    return () => {
      if (Target.__MOBILE__ && !isEmbedded) {
        const metaTagToDelete = document.getElementById('override-viewport-tag');
        metaTagToDelete && metaTagToDelete.parentNode.removeChild(metaTagToDelete);
      }
    };
  }, [isFullscreen]);

  return (
    <Wrapper
      ref={wrapperDomNodeRef}
      $offsetVideoLeft={offsetVideoLeft}
      $isPanelVisible={isPanelVisible}
      $isFullscreen={isFullscreen}
    >
      <VideoPlayerContainer
        matchId={matchId}
        streamData={streamData}
        fetchUrl={fetchUrl}
        fetchConfig={fetchConfig}
        pause={pause}
        canShowControls={
          !isFullscreen ||
          screenOrientation === 2 ||
          (screenOrientation === 1 && panelStatus === panelStatuses.HIDDEN) ||
          (screenOrientation === 1 &&
            (basketStatus === basketStatuses.PENDING || basketStatus === basketStatuses.WAITING_TRADER_VALIDATION))
        }
        isPiPFeatureEnabled={!isHighlight}
        isFullscreenAvailable
        isFullscreen={isFullscreen}
        isMultiview={isMultiview}
        isVolumeAvailable
        toggleFullscreen={toggleFullscreen}
        enterFullscreen={enterFullscreen}
        exitFullscreen={exitFullscreen}
        onClick={handleImmersivePlayer}
        disableExport={isHighlight}
      />
      {canDisplayPanel ? (
        <PanelContainer
          matchId={matchId}
          panelStatus={panelStatus}
          setPanelStatus={setPanelStatus}
          resetPanelContent={resetPanelContent}
        >
          {shouldShowBasket ? <ImmersiveBasketContainer /> : <ImmersiveOutcomesListContainer matchId={matchId} />}
        </PanelContainer>
      ) : null}
    </Wrapper>
  );
};

export default ImmersivePlayer;
